import React, { cloneElement, forwardRef } from 'react';
import { styled, InputAdornment, TextField } from '@mui/material';

import { useViewport } from 'contexts/viewport';

const CustomTextField = styled(TextField)(({ $disabled, multiline, size, theme }) => {
    const fontStyles = {
        small: {
            fontSize: `${theme.typography.p3.fontSize.small}rem`,
            fontWeight: theme.typography.p3.fontWeight.regular,
            letterSpacing: `${theme.typography.p3.letterSpacing.small}rem`,
            lineHeight: `${theme.typography.getLineHeight('p3', 'small')}rem`,
        },
        medium: {
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('p2', 'large')}rem`,
        },
        large: {
            fontSize: `${theme.typography.p1.fontSize.large}rem`,
            fontWeight: theme.typography.p1.fontWeight.regular,
            letterSpacing: `${theme.typography.p1.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('p1', 'large')}rem`,
        },
    }[size];

    const inputStyles = {
        small: {
            height: '28px',
            padding: '6px 12px',
        },
        medium: {
            height: '36px',
            padding: '10px 12px',
        },
        large: {
            height: '48px',
            padding: '14px 12px',
        },
    }[size];

    const textAreaStyles = {
        small: {
            padding: '6px 12px',
        },
        medium: {
            padding: '10px 12px',
        },
        large: {
            padding: '14px 12px',
        },
    }[size];

    const helperTextStyles = {
        small: {
            fontSize: `${theme.typography.p4.fontSize.small}rem`,
            fontWeight: theme.typography.p4.fontWeight.regular,
            letterSpacing: `${theme.typography.p4.letterSpacing.small}rem`,
            lineHeight: `${theme.typography.getLineHeight('p4', 'small')}rem`,
        },
        medium: {
            fontSize: `${theme.typography.p3.fontSize.large}rem`,
            fontWeight: theme.typography.p3.fontWeight.regular,
            letterSpacing: `${theme.typography.p3.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('p3', 'large')}rem`,
        },
        large: {
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
            lineHeight: `${theme.typography.getLineHeight('p2', 'large')}rem`,
        },
    }[size];

    return {
        width: '100%',
        '& > label': {
            position: 'unset',
            marginBottom: theme.spacing(1),
            color: theme.palette.ensightful.neutral[900],
            transform: 'none',
            ...fontStyles,
            '&.Mui-error': {
                color: 'initial',
            },
            '&.Mui-focused': {
                color: 'initial',
            },
            '& .MuiInputLabel-asterisk': {
                color: theme.palette.ensightful.error[600],
                ...($disabled && {
                    color: `${theme.palette.ensightful.neutral[400]} !important`,
                }),
            },
            ...($disabled && {
                color: `${theme.palette.ensightful.neutral[400]} !important`,
            }),
        },
        '& > div': {
            background: theme.palette.ensightful.shades[0],
            '& > input': {
                borderRadius: '4px',
                color: theme.palette.ensightful.neutral[900],
                boxSizing: 'border-box',
                ...fontStyles,
                ...inputStyles,
                '&::placeholder': {
                    opacity: 1,
                    color: theme.palette.ensightful.neutral[500],
                },
                ...($disabled && {
                    background: theme.palette.ensightful.neutral[300],
                    color: theme.palette.ensightful.neutral[500],
                    cursor: 'default',
                }),
            },
            '& > fieldset': {
                borderColor: theme.palette.ensightful.neutral[500],
                transition: theme.transitions.default,
                ...($disabled && {
                    borderColor: theme.palette.ensightful.neutral[400],
                }),
                '& > legend': {
                    width: 0,
                },
            },
            '&:focus-within': {
                outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            },
            '&.Mui-error > input ~ fieldset, &.Mui-error > textarea ~ fieldset': {
                border: `1px solid ${theme.palette.ensightful.error[700]} !important`,
                ...($disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
            },
            '&:not(.Mui-error) > input:focus ~ fieldset, &:not(.Mui-error) > textarea:focus ~ fieldset': {
                border: `1px solid ${theme.palette.ensightful.primary[700]} !important`,
                ...($disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
            },
            '&.Mui-error > input:focus ~ fieldset, &.Mui-error > textarea:focus ~ fieldset': {
                border: `1px solid ${theme.palette.ensightful.error[700]} !important`,
                ...($disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
            },
            '&:not(.Mui-error):hover > fieldset': {
                border: `1px solid ${theme.palette.ensightful.primary[700]} !important`,
                ...($disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
            },
            '&.Mui-error:hover > fieldset': {
                border: `1px solid ${theme.palette.ensightful.error[700]} !important`,
                ...($disabled && {
                    border: `1px solid ${theme.palette.ensightful.neutral[400]} !important`,
                }),
            },
            ...(multiline && {
                padding: 0,
                '& > textarea': {
                    borderRadius: '4px',
                    color: theme.palette.ensightful.neutral[900],
                    boxSizing: 'border-box',
                    ...fontStyles,
                    ...textAreaStyles,
                    '&::placeholder': {
                        opacity: 1,
                        color: theme.palette.ensightful.neutral[500],
                    },
                    ...($disabled && {
                        background: theme.palette.ensightful.neutral[300],
                        color: theme.palette.ensightful.neutral[500],
                        cursor: 'default',
                    }),
                },
            }),
        },
        '& > p': {
            margin: theme.spacing(1, 0, 0),
            color: theme.palette.ensightful.neutral[700],
            ...helperTextStyles,
            '&.Mui-error': {
                color: theme.palette.ensightful.error[700],
            },
            ...($disabled && {
                color: `${theme.palette.ensightful.neutral[400]} !important`,
            }),
        },
    };
});

export default forwardRef(
    ({ disabled, disablePointerEventsOnIcon = true, endIcon, multiline, size, startIcon, ...rest }, ref) => {
        const { device } = useViewport();

        const iconInputProps = {};
        const iconSize = {
            mobile: 'medium',
            tablet: 'medium',
            desktop: 'medium',
        }[device];
        if (endIcon) {
            iconInputProps.endAdornment = (
                <InputAdornment disablePointerEvents={disablePointerEventsOnIcon} position={'end'}>
                    {cloneElement(endIcon, {
                        sx: { fontSize: '1rem', color: (theme) => theme.palette.ensightful.neutral[700] },
                        edge: 'end',
                        size: iconSize,
                    })}
                </InputAdornment>
            );
        }
        if (startIcon) {
            iconInputProps.startAdornment = (
                <InputAdornment disablePointerEvents={disablePointerEventsOnIcon} position={'start'}>
                    {cloneElement(startIcon, { sx: { fontSize: '1rem', edge: 'start', size: iconSize } })}
                </InputAdornment>
            );
        }

        const mappedSize =
            size ||
            {
                mobile: 'medium',
                tablet: 'medium',
                desktop: 'medium',
            }[device];

        return (
            <CustomTextField
                $disabled={disabled}
                InputLabelProps={{ color: 'primary', shrink: true }}
                inputProps={{ 'aria-disabled': disabled }}
                inputRef={ref}
                InputProps={{ readOnly: disabled, ...iconInputProps }}
                multiline={multiline}
                size={mappedSize}
                {...rest}
            />
        );
    }
);
